import { RefObject } from 'react';

import { AtSign } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { ChatContextMenu } from '@/components/chat/chat-context-menu/ChatContextMenu';

type AtProductPopoverSource = 'textarea' | 'button';

interface AtProductPopoverMenuContentProps {
  source: AtProductPopoverSource;
  buttonSide: 'left' | 'bottom';
  commandRef?: RefObject<HTMLDivElement>;
}

export const AtProductPopoverMenuContent = ({
  source,
  buttonSide,
  commandRef,
}: AtProductPopoverMenuContentProps) => (
  <PopoverContent
    className={cn(
      'p-0 relative shadow-none border-0',
      source === 'button' && buttonSide === 'left' && 'bottom-6 left-[-200px]'
    )}
    align="start"
    side={source === 'textarea' ? 'top' : buttonSide}
    sideOffset={8}
    collisionPadding={8}
    avoidCollisions
    onFocusOutside={(event) => event.preventDefault()}
    onCloseAutoFocus={(event) => event.preventDefault()}
  >
    <ChatContextMenu commandRef={commandRef} />
  </PopoverContent>
);

interface AtProductPopoverTriggerProps {
  open: boolean;
  setSource: (source: AtProductPopoverSource) => void;
}

export const AtProductPopoverTrigger = ({
  open,
  setSource,
}: AtProductPopoverTriggerProps) => (
  <Tooltip>
    <PopoverTrigger asChild>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          aria-label="Add context"
          aria-expanded={open}
          variant="outline"
          className="h-7 w-7 ml-2 flex-shrink-0"
          onClick={() => setSource('button')}
        >
          <AtSign className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
    </PopoverTrigger>
    <TooltipContent>
      <p>Add context @</p>
    </TooltipContent>
  </Tooltip>
);

interface AtProductPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setSource: (source: AtProductPopoverSource) => void;
  source: AtProductPopoverSource;
  buttonSide?: 'left' | 'bottom';
  commandRef?: RefObject<HTMLDivElement>;
}

export const AtProductPopover = ({
  open,
  onOpenChange,
  setSource,
  source,
  buttonSide = 'left',
  commandRef,
}: AtProductPopoverProps) => (
  <Popover open={open} onOpenChange={onOpenChange}>
    <AtProductPopoverTrigger open={open} setSource={setSource} />
    <AtProductPopoverMenuContent
      source={source}
      buttonSide={buttonSide}
      commandRef={commandRef}
    />
  </Popover>
);
